
<template>
  <!-- =====================================================
       UNKNOWN FIRST-LEVEL CHARACTER
  ====================================================== -->

  <UnlockCharachterComponent
    v-if="!employee"
    :level="1"
    variant="ceo"
  />

  <!-- =====================================================
       REAL FIRST-LEVEL EMPLOYEE
  ====================================================== -->

  <button
    v-else
    type="button"
    class="structure-ceo"
    :class="{ 'character-dimmed': dimmed }"
    :disabled="!employee.userId"
    :title="employee.userId ? 'Открыть профиль' : undefined"
    @click="openProfile"
  >
    <div class="ceo-glow"></div>
    <div class="ceo-top-accent"></div>

    <!-- LEVEL ICON -->

    <div class="ceo-level-icon">
      <svg viewBox="0 0 24 24" fill="none">
        <path
          d="M12 3L14.7 8.5L20.8 9.4L16.4 13.7L17.4 19.8L12 17L6.6 19.8L7.6 13.7L3.2 9.4L9.3 8.5L12 3Z"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linejoin="round"
        />
      </svg>
    </div>

    <!-- CIRCULAR PORTRAIT -->

    <div class="ceo-portrait">

      <img
        v-if="employee.imageURL && !imageFailed"
        :src="employee.imageURL"
        :alt="employee.name || 'Участник'"
        @error="imageFailed = true"
      />

      <span v-else class="ceo-initials">
        {{ initials }}
      </span>

      <span class="ceo-profile-badge">
        <svg viewBox="0 0 24 24" fill="none">
          <path
            d="M5 12H19M19 12L13 6M19 12L13 18"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>

    </div>

    <!-- INFORMATION -->

    <div class="ceo-information">

      <span class="ceo-role">
        УРОВЕНЬ 01
      </span>

      <strong class="ceo-name">
        {{ employee.name || "Не указано" }}
      </strong>

      <span class="ceo-specialty">
        {{
          employee.specifics &&
          employee.specifics !== "Не указано"
            ? employee.specifics
            : "Не указано"
        }}
      </span>

    </div>

    <!-- FOOTER -->

    <div class="ceo-bottom">

      <span class="ceo-status">

        <span
          class="status-dot"
          :class="statusClass"
        ></span>

        {{ statusText }}

      </span>

      <span class="ceo-arrow">
        <svg viewBox="0 0 24 24" fill="none">
          <path
            d="M5 12H19M19 12L13 6M19 12L13 18"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>

    </div>

    <span class="ceo-sparkle" aria-hidden="true">
      ✦
    </span>

  </button>
</template>

<script setup>
import {
  computed,
  ref,
  watch
} from "vue";

import UnlockCharachterComponent
  from "./UnlockCharachterComponent.vue";

// ============================================================
// PROPS
// ============================================================

const props = defineProps({
  employee: {
    type: Object,
    default: null
  },

  dimmed: {
    type: Boolean,
    default: false
  }
});

// ============================================================
// EMITS
// ============================================================

const emit = defineEmits(["open"]);

// ============================================================
// IMAGE
// ============================================================

const imageFailed = ref(false);

watch(
  () => [
    props.employee?.userId,
    props.employee?.imageURL
  ],
  () => {
    imageFailed.value = false;
  }
);

// ============================================================
// INITIALS
// ============================================================

const initials = computed(() => {
  const parts = String(
    props.employee?.name || ""
  )
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) {
    return "?";
  }

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return (
    parts[0].charAt(0) +
    parts[1].charAt(0)
  ).toUpperCase();
});

// ============================================================
// STATUS
// ============================================================

const statusText = computed(() => {
  const value = props.employee?.isCurrentEmployee;

  if (value === true) {
    return "Работает сейчас";
  }

  if (value === false) {
    return "Работал ранее";
  }

  return "Статус неизвестен";
});

const statusClass = computed(() => {
  const value = props.employee?.isCurrentEmployee;

  if (value === true) {
    return "status-current";
  }

  if (value === false) {
    return "status-former";
  }

  return "status-unknown";
});

// ============================================================
// PROFILE
// ============================================================

function openProfile() {
  if (!props.employee?.userId) {
    return;
  }

  emit("open", props.employee);
}
</script>

<style scoped>

/* ============================================================
   CARD
============================================================ */

.structure-ceo {
  position: relative;

  width: 265px;
  min-height: 275px;

  display: flex;
  flex-direction: column;
  align-items: center;

  padding: 24px 20px 18px;

  overflow: hidden;

  color: var(--text, #243247);

  background:
    linear-gradient(
      145deg,
      var(--surface, #ffffff),
      var(--surface-soft, #f8faff)
    );

  border: 1px solid rgba(211, 164, 83, 0.27);
  border-radius: 32px;

  box-shadow:
    0 16px 44px rgba(126, 102, 61, 0.07);

  font-family: "Eastman-Regular", sans-serif;
  text-align: center;

  cursor: pointer;

  transition:
    transform 0.22s ease,
    border-color 0.22s ease,
    box-shadow 0.22s ease,
    opacity 0.22s ease;
}

.structure-ceo:hover:not(:disabled) {
  transform: translateY(-5px);

  border-color: rgba(211, 164, 83, 0.48);

  box-shadow:
    0 24px 54px rgba(126, 102, 61, 0.14);
}

.structure-ceo:focus-visible {
  outline: 2px solid #d3a453;
  outline-offset: 4px;
}

.structure-ceo:disabled {
  cursor: default;
}

/* ============================================================
   DECORATION
============================================================ */

.ceo-glow {
  position: absolute;

  width: 230px;
  height: 230px;

  top: -160px;
  right: -100px;

  border-radius: 50%;

  background:
    radial-gradient(
      circle,
      rgba(224, 179, 98, 0.2),
      transparent 73%
    );

  pointer-events: none;
}

.ceo-top-accent {
  position: absolute;

  top: 0;

  width: 90px;
  height: 3px;

  border-radius: 0 0 10px 10px;

  background:
    linear-gradient(
      90deg,
      #ad8548,
      #e9c886,
      #ad8548
    );
}

.ceo-sparkle {
  position: absolute;

  top: 19px;
  right: 20px;

  color: rgba(211, 164, 83, 0.43);

  font-size: 17px;
}

/* ============================================================
   LEVEL ICON
============================================================ */

.ceo-level-icon {
  position: relative;
  z-index: 1;

  width: 34px;
  height: 34px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-bottom: 11px;

  background: rgba(213, 164, 75, 0.12);
  color: #c59a50;

  border-radius: 50%;
}

.ceo-level-icon svg {
  width: 19px;
  height: 19px;
}

/* ============================================================
   CIRCULAR PORTRAIT
============================================================ */

.ceo-portrait {
  position: relative;

  width: 100px;
  height: 100px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;

  background:
    linear-gradient(
      145deg,
      #f0e4cb,
      #fff8eb
    );

  color: #b89357;

  box-shadow:
    0 0 0 5px rgba(211, 164, 83, 0.08);
}

.ceo-portrait img {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;

  border-radius: 50%;
}

.ceo-initials {
  font-family: "Eastman-Bold", sans-serif;
  font-size: 31px;
}

/* ============================================================
   PROFILE BADGE
============================================================ */

.ceo-profile-badge {
  position: absolute;

  right: -5px;
  bottom: -3px;

  width: 29px;
  height: 29px;

  display: flex;
  align-items: center;
  justify-content: center;

  background:
    linear-gradient(
      145deg,
      #e0bb73,
      #b58b46
    );

  color: #ffffff;

  border: 3px solid var(--surface, #ffffff);
  border-radius: 50%;

  box-shadow:
    0 4px 12px rgba(126, 102, 61, 0.15);
}

.ceo-profile-badge svg {
  width: 15px;
  height: 15px;
}

/* ============================================================
   INFORMATION
============================================================ */

.ceo-information {
  width: 100%;
  min-width: 0;

  display: flex;
  flex-direction: column;
  align-items: center;

  gap: 7px;

  margin-top: 19px;
}

.ceo-role {
  color: #bd9452;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 9px;

  letter-spacing: 1.2px;
}

.ceo-name {
  width: 100%;

  color: var(--text, #243247);

  font-family: "Eastman-Bold", sans-serif;
  font-size: 14px;

  line-height: 1.4;

  overflow-wrap: anywhere;
}

.ceo-specialty {
  width: 100%;

  color: var(--muted, #8896aa);

  font-size: 10px;
  line-height: 1.5;

  overflow-wrap: anywhere;
}

/* ============================================================
   FOOTER
============================================================ */

.ceo-bottom {
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 7px;

  margin-top: auto;
  padding-top: 14px;
}

.ceo-status {
  display: inline-flex;
  align-items: center;

  gap: 7px;

  color: var(--muted, #8896aa);

  font-family: "Eastman-Medium", sans-serif;
  font-size: 9px;
}

.status-dot {
  width: 6px;
  height: 6px;

  flex-shrink: 0;

  border-radius: 50%;
}

.status-current {
  background: #39af7e;
}

.status-former {
  background: #c29b60;
}

.status-unknown {
  background: #a5b1c1;
}

/* ============================================================
   ROUND ARROW
============================================================ */

.ceo-arrow {
  width: 29px;
  height: 29px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #ba9351;

  background: rgba(211, 164, 83, 0.11);

  border-radius: 50%;

  transition:
    background 0.2s ease,
    transform 0.2s ease;
}

.ceo-arrow svg {
  width: 15px;
  height: 15px;
}

.structure-ceo:hover .ceo-arrow {
  transform: translateX(3px);

  background: rgba(211, 164, 83, 0.2);
}

/* ============================================================
   FILTER
============================================================ */

.character-dimmed {
  opacity: 0.25;
  filter: grayscale(0.7);
}

/* ============================================================
   DARK THEME
============================================================ */

:global(html[data-theme="dark"]) .ceo-portrait,
:global(.structure-page.dark) .ceo-portrait {
  background:
    linear-gradient(
      145deg,
      #453b30,
      #302c28
    );

  color: #d1af74;
}

@media (prefers-reduced-motion: reduce) {
  .structure-ceo,
  .ceo-arrow {
    transition: none;
  }
}
</style>
